/**
 * Activity / UEBA - Phase 3 wiring.
 *
 * Live data sources:
 *   GET /v1/activity/ueba/findings  → recent persisted findings (alerts list)
 *   GET /v1/activity/baselines      → per-actor baselines (KPI counts + outlier scatter)
 *
 * The previous cut had a real findings call (good) but the QPS time
 * series, sigma-band overlay, and population scatter were synthetic.
 * In the Phase-3 wiring pass:
 *
 * • The "actor query rate · last 24 hours" chart now renders per-actor
 *   baselines as horizontal bands rather than a fake QPS time series.
 *   A real time series needs a `/baselines/:actorId/recent-window` route
 *   we have not yet built - see tracker §12 Entry #45 for the
 *   sequencing.
 * • Actor population scatter is computed from real baselines
 *   (queries_per_day × pii_rows_per_day baselines available today).
 * • Alerts list = real UEBA findings, no `ALERTS` fallback.
 */

import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  CartesianGrid, ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, Tooltip as RTooltip,
} from 'recharts';
import { TrendingUp, AlertTriangle, User, Loader2, ArrowUpRight, Sparkles, CheckCircle2, ShieldX, ShieldCheck } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listUebaFindings, listActivityBaselines, analyzeUeba, acknowledgeUebaFinding, getActivitySummary,
  patchUebaFindingStatus,
  type UebaFinding, type ActivityBaseline,
} from '@/lib/api';
import {
  FindingStatusDialog,
  type FindingTransition,
} from '@/components/activity/FindingStatusDialog';

interface AlertView {
  id: string;
  actor: string;
  rule: string;
  severity: 'critical' | 'medium';
  detail: string;
  time: string;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function adaptUebaFinding(f: UebaFinding): AlertView {
  const ruleLabel: Record<string, string> = {
    rule1_3sigma:      'Rule 1 · 3σ',
    rule2_2of3_2sigma: 'Rule 2 · 2/3 in 2σ',
    rule3_4of5_1sigma: 'Rule 3 · 4/5 in 1σ',
    rule4_8onside:     'Rule 4 · 8 on side',
  };
  const z = Number(f.zScore);
  return {
    id: f.id,
    actor: f.actorUserId,
    rule: ruleLabel[f.rule] ?? f.rule,
    severity: f.rule === 'rule1_3sigma' || Math.abs(z) > 3 ? 'critical' : 'medium',
    detail: `${f.metric} observed=${Number(f.observed).toFixed(1)} vs μ=${Number(f.expectedMean).toFixed(1)} σ=${Number(f.expectedStddev).toFixed(2)} (z=${z.toFixed(2)})`,
    time: timeAgo(f.detectedAt),
  };
}

export function ActivityUeba() {
  const tx = useRegulationText();
  const [alerts, setAlerts] = useState<AlertView[]>([]);
  const [counts, setCounts] = useState<Awaited<ReturnType<typeof getActivitySummary>> | null>(null);
  const [baselines, setBaselines] = useState<ActivityBaseline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeStatus, setAnalyzeStatus] = useState<string | null>(null);
  const [ackingId, setAckingId] = useState<string | null>(null);
  const [ackedIds, setAckedIds] = useState<Set<string>>(new Set());

  // Fix D UI (2026-05-28) - dismiss/escalate dialog state. The id +
  // status pair identifies the in-flight transition; transitionedIds
  // hides the row's action buttons once the PATCH lands so an operator
  // doesn't double-submit.
  const [dialogState, setDialogState] = useState<
    | { open: true; id: string; status: FindingTransition; context: string }
    | { open: false }
  >({ open: false });
  const [transitionedIds, setTransitionedIds] = useState<Set<string>>(new Set());

  // Wave 5 (2026-05-27) - per-row acknowledge wires up
  // POST /v1/activity/ueba/findings/:id/acknowledge. SOC operator
  // can claim ownership of a finding without leaving the page.
  async function handleAck(id: string): Promise<void> {
    setAckingId(id);
    try {
      await acknowledgeUebaFinding(id);
      setAckedIds((prev) => new Set([...prev, id]));
    } catch {
      // surface error in analyzeStatus rail for visibility
      setAnalyzeStatus('Acknowledge failed - refresh and try again.');
    } finally {
      setAckingId(null);
    }
  }

  // Fix D UI (2026-05-28) - open the dismiss/escalate dialog. The
  // dialog itself collects the reason; submission flows back through
  // handleTransition below.
  function openTransitionDialog(a: AlertView, status: FindingTransition): void {
    setDialogState({
      open: true,
      id: a.id,
      status,
      context: `${a.actor} · ${a.rule}`,
    });
  }

  async function handleTransition(reason: string): Promise<void> {
    if (!dialogState.open) return;
    const { id, status } = dialogState;
    await patchUebaFindingStatus(id, status, reason);
    // Mark transitioned so the row's Dismiss/Escalate buttons disable.
    // We keep the row visible (don't refresh-and-hide) so the operator
    // sees their action landed before the page churns.
    setTransitionedIds((prev) => new Set([...prev, id]));
  }

  // Wave 2 (2026-05-27) - surface POST /v1/activity/ueba/analyze.
  // Runs the Western-Electric 4-rule detector live over the last
  // 24h vs the 30d baseline. Persists any fired findings into the
  // ueba_findings table so the list below refreshes with new rows.
  async function handleAnalyze(): Promise<void> {
    setAnalyzing(true);
    setAnalyzeStatus(null);
    try {
      const result = await analyzeUeba({ recentHours: 24, baselineDays: 30 });
      const n = Array.isArray(result.findings) ? result.findings.length : 0;
      setAnalyzeStatus(
        n === 0
          ? 'Analysis complete - no new outliers in the last 24h.'
          : `Analysis complete - ${n} new outlier${n === 1 ? '' : 's'} detected.`,
      );
      await refresh();
    } catch (e) {
      setAnalyzeStatus(e instanceof Error ? `Analysis failed: ${e.message}` : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([
      listUebaFindings(),
      listActivityBaselines(),
      getActivitySummary(),
    ]).then(([findRes, baseRes, sumRes]) => {
      if (signal?.aborted) return;
      if (sumRes.status === 'fulfilled') setCounts(sumRes.value);
      const fnds = findRes.status === 'fulfilled' ? findRes.value.findings.map(adaptUebaFinding) : [];
      const bls = baseRes.status === 'fulfilled' ? baseRes.value.baselines : [];
      const errs: string[] = [];
      if (findRes.status === 'rejected') errs.push(`findings: ${(findRes.reason as Error)?.message ?? 'failed'}`);
      if (baseRes.status === 'rejected') errs.push(`baselines: ${(baseRes.reason as Error)?.message ?? 'failed'}`);
      setAlerts(fnds);
      setBaselines(bls);
      setError(errs.length > 0 ? errs.join(' · ') : null);
      setLoading(false);
    });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // Derived counts.
  const distinctActors = useMemo(() => new Set(baselines.map((b) => b.actorId)), [baselines]);
  // Counted by the server over every UEBA finding (release 2.1 item 4); these
  // used to count the 200-row list above.
  const recent24hCount = counts?.uebaLast24h ?? 0;
  const threeSigmaCount = counts?.uebaBySeverity['critical'] ?? 0;

  // Scatter data: queries_per_day × pii_rows_per_day baselines, one
  // point per actor that has both metrics on file.
  const scatterPoints = useMemo(() => {
    const byActor: Record<string, { qpd?: number; pii?: number }> = {};
    for (const b of baselines) {
      const v = parseFloat(b.baselineValue);
      if (!Number.isFinite(v)) continue;
      const entry = byActor[b.actorId] ?? {};
      if (b.baselineMetric === 'queries_per_day') entry.qpd = v;
      if (b.baselineMetric === 'pii_rows_per_day') entry.pii = v;
      byActor[b.actorId] = entry;
    }
    const points: Array<{ x: number; y: number; actor: string }> = [];
    for (const [actor, m] of Object.entries(byActor)) {
      if (m.qpd != null && m.qpd > 0) {
        const piiRatio = m.pii != null ? Math.min(1, m.pii / m.qpd) : 0;
        points.push({ x: m.qpd, y: piiRatio, actor });
      }
    }
    return points;
  }, [baselines]);

  const outliers = useMemo(() => scatterPoints.filter((p) => p.y > 0.6), [scatterPoints]);
  const normals  = useMemo(() => scatterPoints.filter((p) => p.y <= 0.6), [scatterPoints]);

  return (
    <PageContainer>
      <PageHeader
        title="UEBA Baselines"
        subtitle="Per-actor behavioral baselines using Western Electric Rules. 4-rule statistical control detects 3σ outliers + 2-of-3-in-2σ + 4-of-5-in-1σ + 8-on-side."
        badges={[
          { label: 'Western Electric', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: `${distinctActors.size} actors baselined`, variant: 'info' as const }]),
        ]}
        actions={
          <Button
            size="sm"
            onClick={() => void handleAnalyze()}
            disabled={analyzing}
            title="Run the Western-Electric 4-rule detector over the last 24h vs the 30d baseline"
            data-action="disc.dam_ueba.a02"
          >
            <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${analyzing ? 'animate-pulse' : ''}`} />
            {analyzing ? 'Analyzing…' : 'Analyze now'}
          </Button>
        }
      />
      {analyzeStatus && (
        <div className="glass-lite rounded-md p-3 mb-3 text-xs text-muted-foreground">
          {analyzeStatus}
        </div>
      )}

      {loading && <LoadingState caption="Loading UEBA findings + baselines…" />}
      {error && !loading && (
        <ErrorState
          title="Some UEBA data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Actors baselined" value={distinctActors.size} icon={User} />
            <KpiCard
              label="Recent alerts"
              value={recent24hCount}
              icon={AlertTriangle}
              glow={recent24hCount > 0 ? 'medium' : 'low'}
              hint="last 24h"
            />
            <KpiCard
              label="3σ outliers"
              value={threeSigmaCount}
              icon={TrendingUp}
              glow={threeSigmaCount > 0 ? 'high' : 'low'}
              hint="Rule 1 fires"
            />
            <KpiCard
              label="Baselines on file"
              value={baselines.length}
              icon={User}
              hint="across all metrics"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Recent UEBA alerts</CardTitle>
                <CardDescription clamp>
                  Each alert names the Western Electric rule that fired against an actor's baseline metric.
                </CardDescription>
              </CardHeader>
              {alerts.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center">
                  No UEBA findings on file yet. As the analyzer runs against the activity stream, alerts will appear here.
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.slice(0, 8).map((a, i) => (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="glass-lite p-3 rounded-md flex items-start gap-3"
                    >
                      <span className={`h-2 w-2 rounded-full mt-1.5 ${a.severity === 'critical' ? 'bg-sev-critical animate-pulse' : 'bg-sev-medium'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="font-mono text-xs">{a.actor}</code>
                          <Badge variant={a.severity as 'critical' | 'medium'} className="text-[9px]">{a.rule}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{a.detail}</p>
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">{a.time}</p>
                      </div>
                      {/* P1-5 (2026-05-26) - operator can jump from a UEBA
                          finding to the Decisions surface filtered by the
                          actor name. Decisions page reads ?q= to prefill
                          its existing free-text filter. */}
                      <div className="flex flex-col gap-1 shrink-0 self-center">
                        <Button
                          size="sm"
                          variant={ackedIds.has(a.id) ? 'glass' : 'default'}
                          onClick={() => void handleAck(a.id)}
                          disabled={ackedIds.has(a.id) || ackingId === a.id || transitionedIds.has(a.id)}
                          title="Claim ownership of this finding (audit-chained)"
                          className="h-7 text-[10px]"
                          data-action="disc.dam_ueba.a01"
                        >
                          {ackingId === a.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3 w-3" />
                          )}
                          {ackedIds.has(a.id) ? 'Acked' : 'Ack'}
                        </Button>
                        {/* Fix D UI (2026-05-28) - dismiss / escalate
                            (regulator-grade reason captured into the
                            audit chain via PATCH .../status). */}
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openTransitionDialog(a, 'dismissed')}
                            disabled={transitionedIds.has(a.id)}
                            title="Dismiss as false-positive (reason captured in audit chain)"
                            className="h-6 text-[10px] px-1.5 flex-1"
                            data-action="disc.dam_ueba.a03"
                          >
                            <ShieldX className="h-3 w-3" />
                            Dismiss
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openTransitionDialog(a, 'escalated')}
                            disabled={transitionedIds.has(a.id)}
                            title="Escalate to incident-response (reason captured in audit chain)"
                            className="h-6 text-[10px] px-1.5 flex-1"
                            data-action="disc.dam_ueba.a04"
                          >
                            <ShieldCheck className="h-3 w-3" />
                            Escalate
                          </Button>
                        </div>
                        <Link
                          to="/detection/decisions"
                          search={{ q: a.actor } as never}
                          className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5 justify-end"
                          title={`See decisions involving ${a.actor}`}
                        >
                          Decisions <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Actor population scatter</CardTitle>
                <CardDescription>
                  <Rx>X = queries-per-day baseline. Y = pii-rows-per-day ÷ queries (saturated at 1.0). Red points = high-PII-touch outliers.</Rx>
                </CardDescription>
              </CardHeader>
              {scatterPoints.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center">
                  Scatter populates once at least one actor has both <code>queries_per_day</code> and <code>pii_rows_per_day</code> baselines on file.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis
                      type="number"
                      dataKey="x"
                      name="Queries / day"
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      stroke="hsl(var(--border))"
                      label={{ value: 'Queries / day', position: 'insideBottom', offset: -5, fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name={tx("PII ratio")}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      stroke="hsl(var(--border))"
                      label={{ value: tx('PII touched ratio'), angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    />
                    <RTooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
                      content={({ payload }) => {
                        const p = payload?.[0]?.payload as { x: number; y: number; actor: string } | undefined;
                        if (!p) return null;
                        return (
                          <div className="glass-strong px-2 py-1.5 text-xs">
                            <div className="font-mono">{p.actor}</div>
                            <div className="text-muted-foreground">
                              {Math.round(p.x).toLocaleString('en-IN')} q/d · {(p.y * 100).toFixed(0)}% <Rx>PII</Rx>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Scatter data={normals} fill="hsl(var(--chart-2))" />
                    <Scatter data={outliers} fill="hsl(var(--sev-critical))" />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </GlassCard>
          </div>
        </>
      )}

      {/* Fix D UI (2026-05-28) - dismiss / escalate dialog. Always
          rendered so Radix's Portal + Overlay stay mounted; controlled
          via the `open` prop. Matches the existing Findings.tsx pattern
          which sidesteps the mount/unmount race that causes Radix to
          render the overlay without the content (observed 2026-05-28
          browser smoke). */}
      <FindingStatusDialog
        open={dialogState.open}
        status={dialogState.open ? dialogState.status : 'dismissed'}
        context={dialogState.open ? dialogState.context : ''}
        onSubmit={handleTransition}
        onClose={() => setDialogState({ open: false })}
      />
    </PageContainer>
  );
}
